/* eslint-disable react/jsx-no-bind */
import { useState } from "react";

import { Box, Grid, Stack } from "@mui/material";
import { EmailContentType, Emptyable } from "@simplyk/common";
import classNames from "classnames";
import { UseFormReturn } from "react-hook-form";

import { useCurrentUserContext } from "../../../../contexts/CurrentUserContext";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import { useTranslate } from "../../../../hooks/useTranslate";
import { EmailEditorFormData } from "../../../../types/email";
import { RichEmailFooterAction, useEmailFooter } from "../../hooks/useEmailFooter";
import { EmailContent } from "../../types";
import { useStyles } from "../../useStyles";

import { DropdownButton } from "@/components/DropdownButton/DropdownButton";
import { MoreButtonsDrawer } from "@/components/MoreButtonsDrawer/MoreButtonsDrawer";
import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import { DashboardTooltip } from "@/dashboard-design-system/Tooltip/DashboardTooltip";
import { MoreHorizontal, Sparks } from "@/icons/outlined";

type GenericAction = RichEmailFooterAction & {
  onClick: () => void;
  children: React.ReactNode;
};

interface EmailFooterProps {
  form: UseFormReturn<EmailEditorFormData>;
  handleSendEmail: () => void;
  loading?: boolean;
  emailType: Emptyable<EmailContentType>;
  emailContent: EmailContent;
  isGenerating?: boolean;
  generateEmail: (transactionId: string, length?: "shorter" | "longer" | "regenerate") => Promise<void>;
}

export const EmailFooter = ({
  form,
  handleSendEmail,
  loading,
  emailType,
  emailContent,
  isGenerating = false,
  generateEmail: generateEmailProp,
}: EmailFooterProps) => {
  const { isSmallScreen } = useMediaQuery();
  const { currentUser } = useCurrentUserContext();
  const classes = useStyles();
  const { t } = useTranslate();

  const { actions, disableActions, generatingEmail } = useEmailFooter({
    footerActions: emailContent.thankYouTransactionId
      ? undefined // Thank you email - let useEmailFooter show AI buttons based on thankYouTransactionId
      : ["send"],
    form,
    handleSendEmail,
    loading,
    emailType,
    emailContent,
    isGenerating,
    generateEmail: generateEmailProp,
  });

  if (disableActions) {
    return null;
  }

  const { visible, extra } = splitActions(actions, isSmallScreen, Boolean(emailContent.thankYouTransactionId));

  const extraActions: GenericAction[] = extra.map((ex) => {
    return {
      ...ex,
      children: (
        <Stack direction="row" alignItems="center" gap={1} width="100%">
          {ex.startIcon && (
            <Box display="flex" alignItems="center" minWidth={16} minHeight={16}>
              {ex.startIcon}
            </Box>
          )}
          <Box component="span" flex={1}>
            {ex.label || "button"}
          </Box>
        </Stack>
      ),
      onClick: ex.onClick as () => void,
    };
  });

  return (
    <Grid
      item
      xs={12}
      alignContent="flex-end"
      className={classNames({
        [classes.modalBottomButtonWhenVerified]: currentUser?.emailVerified,
      })}
    >
      <Grid container justifyContent="flex-start">
        {visible.map(
          ({ key, onClick, disabled, label: children, tooltipText, startIcon: _startIcon, ...rest }, index) => {
            return (
              <Grid
                item
                key={key}
                xs={isSmallScreen ? 12 : undefined}
                marginRight={isSmallScreen ? 0 : 1}
                marginBottom={isSmallScreen ? 1 : 0}
              >
                <DashboardTooltip title={tooltipText}>
                  <Box>
                    <DashboardButton
                      data-test={`email-editor-${key}-button`}
                      variant={index === 0 ? "filled" : "outlined"}
                      vibe={index === 0 ? "brand" : "neutral"}
                      fullWidth
                      onClick={onClick as () => void}
                      disabled={disabled}
                      {...rest}
                    >
                      {children}
                    </DashboardButton>
                  </Box>
                </DashboardTooltip>
              </Grid>
            );
          }
        )}
        <Grid
          item
          key="more"
          xs={isSmallScreen ? 12 : undefined}
          marginRight={isSmallScreen ? 0 : 1}
          marginBottom={isSmallScreen ? 1 : 0}
        >
          {extraActions.length > 0 && !isSmallScreen && (
            <DropdownButton
              size="medium"
              variant="outlined"
              vibe="neutral"
              placement="top-end"
              menuItemsProps={extraActions}
              disabled={emailContent.thankYouTransactionId ? generatingEmail : false}
              {...(emailContent.thankYouTransactionId
                ? {
                    startIcon: <Sparks />,
                    buttonText: t("dashboard", "EmailDrawer.actions.refineContent"),
                  }
                : {
                    onlyIcon: <MoreHorizontal />,
                  })}
            />
          )}
          {isSmallScreen && <MobileMoreActions extraActions={extraActions} />}
        </Grid>
      </Grid>
    </Grid>
  );
};

const MobileMoreActions = ({ extraActions }: { extraActions: GenericAction[] }) => {
  const { t } = useTranslate();
  const [isMoreButtonDrawerOpen, setMoreButtonDrawerOpen] = useState(false);
  if (!extraActions.length) {
    return null;
  }

  const mobileExtraMenu = [
    {
      buttonText: t("dashboard", "paymentsPage.header.fromOptions"),
      dropdownButtonOptions: extraActions,
    },
  ];

  return (
    <Grid item key="more" xs={12} marginBottom={1}>
      <MoreButtonsDrawer
        title={t("dashboard", "EmailFooter.mobile.actions.otherSendingOptions")}
        buttons={mobileExtraMenu}
        isOpen={isMoreButtonDrawerOpen}
        setIsOpen={setMoreButtonDrawerOpen}
      />
      <DashboardButton variant="outlined" vibe="neutral" fullWidth onClick={() => setMoreButtonDrawerOpen(true)}>
        {t("dashboard", "EmailFooter.mobile.actions.otherSendingOptions")}
      </DashboardButton>
    </Grid>
  );
};

const splitActions = (
  actions: RichEmailFooterAction[],
  isSmallScreen: boolean,
  hasThankYouTransactionId: boolean = false
): {
  visible: RichEmailFooterAction[];
  extra: RichEmailFooterAction[];
} => {
  if (isSmallScreen) {
    const [visible, ...extra] = actions;
    return { visible: [visible], extra };
  }
  if (hasThankYouTransactionId) {
    // For thank you emails, show first button (send) as visible, rest (AI buttons) in dropdown
    return { visible: actions.slice(0, 1), extra: actions.slice(1) };
  } else {
    if (actions.length > 3) {
      return { visible: actions.slice(0, 2), extra: actions.slice(2) };
    } else {
      return { visible: actions, extra: [] };
    }
  }
};
